import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, FileText, Download, Pencil, Trash2, Search, CheckCircle2, Undo2, Clock, AlertTriangle, Ban, BellRing, FileArchive, Landmark, X, Copy } from "lucide-react";
import api from "@/lib/api";
import { eur, deDate, yearOf, isOverdue, badgeFor, STATUS } from "@/lib/format";
import { VatQuarterly } from "@/components/VatQuarterly";
import { useAuth } from "@/context/AuthContext";
// STATUS retained for potential reuse; badge rendering uses badgeFor

const TABS = [
  { key: "alle", label: "Alle" },
  { key: "offen", label: "Offen" },
  { key: "bezahlt", label: "Bezahlt" },
  { key: "storniert", label: "Storniert" },
];

export default function Invoices() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { readOnly } = useAuth();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("alle");
  const [year, setYear] = useState("alle");

  const { data: invoices = [], isLoading } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/invoices/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Rechnung gelöscht");
    },
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/invoices/${id}/status`, { status }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      const msg =
        vars.status === "bezahlt" ? "Zahlungseingang bestätigt"
        : vars.status === "storniert" ? "Rechnung storniert & archiviert"
        : "Als offen markiert";
      toast.success(msg);
    },
    onError: () => toast.error("Status konnte nicht geändert werden."),
  });

  const sendReminder = useMutation({
    mutationFn: (id) => api.post(`/invoices/${id}/reminder`),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      toast.success("Zahlungserinnerung erstellt");
      navigate(`/invoices/${id}/reminder/print`);
    },
    onError: () => toast.error("Mahnung konnte nicht erstellt werden."),
  });

  const [exporting, setExporting] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [vatOpen, setVatOpen] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/invoices/export", { params: { year }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rechnungen-${year === "alle" ? "alle" : year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export erstellt");
    } catch {
      toast.error("Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  };

  const exportZip = async () => {
    setZipping(true);
    try {
      const { data } = await api.get("/invoices/export-zip", { params: { year }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rechnungen-${year === "alle" ? "alle" : year}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF-Paket erstellt");
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Keine Rechnungen für diesen Zeitraum." : "PDF-Export fehlgeschlagen");
    } finally {
      setZipping(false);
    }
  };

  const years = useMemo(() => {
    const set = new Set(invoices.map((i) => yearOf(i.invoice_date)).filter((y) => y !== "—"));
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [invoices]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return invoices.filter((i) => {
      if (tab === "alle" && i.status === "storniert") return false;
      if (tab === "offen" && i.status !== "offen") return false;
      if (tab === "bezahlt" && i.status !== "bezahlt") return false;
      if (tab === "storniert" && i.status !== "storniert") return false;
      if (year !== "alle" && yearOf(i.invoice_date) !== year) return false;
      if (s && !(i.number?.toLowerCase().includes(s) || (i.customer?.company_name || "").toLowerCase().includes(s) || (i.customer?.contact || "").toLowerCase().includes(s))) return false;
      return true;
    });
  }, [invoices, q, tab, year]);

  // group by year (desc)
  const groups = useMemo(() => {
    const map = {};
    for (const inv of filtered) {
      const y = yearOf(inv.invoice_date);
      (map[y] = map[y] || []).push(inv);
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const openSum = filtered.filter((i) => i.status === "offen").reduce((s, i) => s + (i.gross || 0), 0);
  const paidSum = filtered.filter((i) => i.status === "bezahlt").reduce((s, i) => s + (i.gross || 0), 0);
  const overdueSum = filtered.filter(isOverdue).reduce((s, i) => s + (i.gross || 0), 0);
  const overdueCount = filtered.filter(isOverdue).length;

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Rechnungen</h1>
          <p className="text-sm text-slate-500 mt-1">Offene und bezahlte Rechnungen – nach Jahren gegliedert.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setVatOpen(true)}
            data-testid="vat-quarterly-button"
            className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm transition-colors"
          >
            <Landmark className="h-4 w-4" /> USt. Quartal
          </button>
          <button
            onClick={exportCsv}
            disabled={exporting}
            data-testid="export-csv-button"
            className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm transition-colors disabled:opacity-60"
          >
            <Download className="h-4 w-4" /> {exporting ? "Exportiert…" : `CSV-Export${year !== "alle" ? ` ${year}` : ""}`}
          </button>
          <button
            onClick={exportZip}
            disabled={zipping}
            data-testid="export-zip-button"
            className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm transition-colors disabled:opacity-60"
          >
            <FileArchive className="h-4 w-4" /> {zipping ? "Erstellt…" : `PDF-Paket${year !== "alle" ? ` ${year}` : ""}`}
          </button>
          <button
            onClick={() => navigate("/invoices/new")}
            disabled={readOnly}
            title={readOnly ? "Nur-Lesen-Modus – Abo erforderlich" : undefined}
            data-testid="create-invoice-button"
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" /> Neue Rechnung
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Chip label="Offen" value={eur(openSum)} icon={Clock} tint="text-amber-600" testid="sum-open" />
        <Chip label="Überfällig" value={eur(overdueSum)} icon={AlertTriangle} tint="text-red-600" testid="sum-overdue" hint={overdueCount ? `${overdueCount} Rechnung(en)` : "keine"} />
        <Chip label="Bezahlt" value={eur(paidSum)} icon={CheckCircle2} tint="text-emerald-600" testid="sum-paid" />
        <Chip label="Angezeigt" value={`${filtered.length}`} icon={FileText} tint="text-slate-700" testid="sum-count" hint="Rechnung(en)" />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1" data-testid="status-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`filter-tab-${t.key}`}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.key ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          data-testid="year-select"
          className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        >
          <option value="alle">Alle Jahre</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="invoice-search"
            placeholder="Nach Nummer oder Kunde suchen…"
            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Grouped list */}
      {isLoading ? (
        <div className="bg-white border border-slate-200/80 rounded-xl px-5 py-14 text-center text-sm text-slate-400">Lädt…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200/80 rounded-xl px-5 py-16 text-center">
          <FileText className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Keine Rechnungen in dieser Auswahl.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([y, list]) => {
            const total = list.reduce((s, i) => s + (i.gross || 0), 0);
            const open = list.filter((i) => i.status === "offen").reduce((s, i) => s + (i.gross || 0), 0);
            const overdue = list.filter(isOverdue).reduce((s, i) => s + (i.gross || 0), 0);
            return (
              <div key={y} data-testid={`year-group-${y}`}>
                <div className="flex items-baseline justify-between px-1 mb-2">
                  <h2 className="font-display text-lg font-bold tracking-tight">{y}</h2>
                  <div className="text-xs text-slate-500">
                    {list.length} Rechnung(en) · Summe <span className="font-mono tabular-nums text-slate-700">{eur(total)}</span>
                    {open > 0 && <> · offen <span className="font-mono tabular-nums text-amber-600">{eur(open)}</span></>}
                    {overdue > 0 && <> · überfällig <span className="font-mono tabular-nums text-red-600">{eur(overdue)}</span></>}
                  </div>
                </div>
                <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden divide-y divide-slate-100">
                  <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-2.5 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    <span className="col-span-2">Nummer</span>
                    <span className="col-span-4">Kunde</span>
                    <span className="col-span-2">Datum</span>
                    <span className="col-span-2">Status</span>
                    <span className="col-span-2 text-right">Betrag</span>
                  </div>
                  {list.map((inv) => {
                    const st = badgeFor(inv);
                    const isPaid = inv.status === "bezahlt";
                    const od = isOverdue(inv);
                    return (
                      <div key={inv.id} className={`grid grid-cols-12 gap-4 px-5 py-3.5 items-center transition-colors group ${od ? "bg-red-50/50 hover:bg-red-50" : "hover:bg-slate-50"}`} data-testid={`invoice-row-${inv.number}`}>
                        <span className="col-span-6 md:col-span-2 font-mono text-sm font-semibold">{inv.number}</span>
                        <span className="col-span-6 md:col-span-4 text-sm text-slate-700 truncate">{inv.customer?.company_name || inv.customer?.contact || "—"}</span>
                        <span className="hidden md:block col-span-2 text-sm text-slate-500">
                          {deDate(inv.invoice_date)}
                          {isPaid && inv.paid_date && <span className="block text-xs text-emerald-600">bezahlt {deDate(inv.paid_date)}</span>}
                          {od && inv.due_date && <span className="block text-xs text-red-600">fällig war {deDate(inv.due_date)}</span>}
                          {inv.reminder_level > 0 && <span className="block text-xs text-red-600">Mahnstufe {inv.reminder_level} · {deDate(inv.reminder_date)}</span>}
                        </span>
                        <span className="col-span-6 md:col-span-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                        </span>
                        <div className="col-span-6 md:col-span-2 flex items-center justify-end gap-1">
                          <span className="font-mono tabular-nums text-sm font-semibold mr-1">{eur(inv.gross)}</span>
                          {readOnly ? (
                            <IconBtn title="PDF / Drucken" onClick={() => navigate(`/invoices/${inv.id}/print`)} testid={`export-pdf-${inv.number}`}><Download className="h-4 w-4" /></IconBtn>
                          ) : inv.status === "storniert" ? (
                            <>
                              <IconBtn title="Storno aufheben" onClick={() => setStatus.mutate({ id: inv.id, status: "offen" })} testid={`unstorno-${inv.number}`}><Undo2 className="h-4 w-4" /></IconBtn>
                              <IconBtn title="Duplizieren" onClick={() => navigate(`/invoices/new?from=${inv.id}`)} testid={`duplicate-invoice-${inv.number}`}><Copy className="h-4 w-4" /></IconBtn>
                              <IconBtn title="PDF / Drucken" onClick={() => navigate(`/invoices/${inv.id}/print`)} testid={`export-pdf-${inv.number}`}><Download className="h-4 w-4" /></IconBtn>
                            </>
                          ) : (
                            <>
                              {isPaid ? (
                                <IconBtn title="Als offen markieren" onClick={() => setStatus.mutate({ id: inv.id, status: "offen" })} testid={`mark-open-${inv.number}`}><Undo2 className="h-4 w-4" /></IconBtn>
                              ) : (
                                <IconBtn title="Zahlungseingang bestätigen" onClick={() => setStatus.mutate({ id: inv.id, status: "bezahlt" })} testid={`mark-paid-${inv.number}`} positive><CheckCircle2 className="h-4 w-4" /></IconBtn>
                              )}
                              {od && (
                                <IconBtn title="Zahlungserinnerung / Mahnung erstellen" onClick={() => sendReminder.mutate(inv.id)} testid={`mahnung-${inv.number}`} warn><BellRing className="h-4 w-4" /></IconBtn>
                              )}
                              <IconBtn title="PDF / Drucken" onClick={() => navigate(`/invoices/${inv.id}/print`)} testid={`export-pdf-${inv.number}`}><Download className="h-4 w-4" /></IconBtn>
                              <IconBtn title="Bearbeiten" onClick={() => navigate(`/invoices/${inv.id}/edit`)} testid={`edit-invoice-${inv.number}`}><Pencil className="h-4 w-4" /></IconBtn>
                              <IconBtn title="Duplizieren" onClick={() => navigate(`/invoices/new?from=${inv.id}`)} testid={`duplicate-invoice-${inv.number}`}><Copy className="h-4 w-4" /></IconBtn>
                              <IconBtn title="Stornieren" onClick={() => { if (window.confirm(`Rechnung ${inv.number} stornieren? Sie wird archiviert und behält ihre Nummer.`)) setStatus.mutate({ id: inv.id, status: "storniert" }); }} testid={`storno-${inv.number}`} danger><Ban className="h-4 w-4" /></IconBtn>
                              <IconBtn title="Löschen" onClick={() => { if (window.confirm(`Rechnung ${inv.number} löschen?`)) del.mutate(inv.id); }} testid={`delete-invoice-${inv.number}`} danger><Trash2 className="h-4 w-4" /></IconBtn>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {vatOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm px-4" data-testid="vat-dialog">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-display font-semibold text-base inline-flex items-center gap-2">
                <Landmark className="h-4 w-4 text-blue-600" /> Umsatzsteuer-Voranmeldung · Quartale
              </h3>
              <button onClick={() => setVatOpen(false)} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100" data-testid="vat-dialog-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <VatQuarterly initialYear={year !== "alle" ? Number(year) : new Date().getFullYear()} showYearSelector />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, icon: Icon, tint, testid, hint }) {
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${tint}`} />
      </div>
      <div className={`mt-1.5 text-lg font-bold font-mono tabular-nums ${tint}`}>{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function IconBtn({ children, danger, positive, warn, testid, ...props }) {
  const tone = danger
    ? "hover:text-red-600 hover:bg-red-50"
    : positive
    ? "hover:text-emerald-600 hover:bg-emerald-50"
    : warn
    ? "hover:text-amber-600 hover:bg-amber-50"
    : "hover:text-slate-900 hover:bg-slate-100";
  return (
    <button data-testid={testid} className={`h-8 w-8 grid place-items-center rounded-lg text-slate-400 transition-colors ${tone}`} {...props}>
      {children}
    </button>
  );
}
